import { useCallback, useEffect, useState } from "react";
import type { DirectoryUserDto, FollowUpTaskDto } from "@grizzly/shared";
import { taskPriority, taskStatus } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { formatDateTime } from "../../lib/format.js";
import { CheckCircle2, Plus, RotateCcw } from "lucide-react";

interface Form {
  subject: string;
  notes: string;
  assigneeId: string;
  dueDate: string;
  priority: string;
}

const EMPTY_FORM: Form = {
  subject: "",
  notes: "",
  assigneeId: "",
  dueDate: "",
  priority: "medium",
};

function dateToIso(date: string): string | undefined {
  if (!date) return undefined;
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y!, m! - 1, d!, 23, 59, 59).toISOString();
}

export function TaskSection({ customerId }: { customerId: string }) {
  const { push } = useToast();
  const { t } = useI18n();
  const [tasks, setTasks] = useState<FollowUpTaskDto[]>([]);
  const [directory, setDirectory] = useState<DirectoryUserDto[]>([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [taskRes, dirRes] = await Promise.all([
        api.listTasks({ customerId }),
        api.listDirectory(),
      ]);
      setTasks(taskRes.items);
      setDirectory(dirRes.items);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.createTask({
        customerId,
        assigneeId: form.assigneeId || "",
        subject: form.subject,
        notes: form.notes || "",
        dueAt: dateToIso(form.dueDate),
        priority: form.priority,
      });
      push(t("customers.tasks.scheduledToast"), form.subject);
      setForm(EMPTY_FORM);
      setAdding(false);
      await load();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [customerId, form, load, push, t]);

  const complete = useCallback(
    async (task: FollowUpTaskDto) => {
      try {
        await api.completeTask(task.id);
        push(t("customers.tasks.completedToast"), task.subject);
        await load();
      } catch (err) {
        if (err instanceof ApiClientError) push(err.message, undefined, "danger");
      }
    },
    [load, push, t]
  );

  const reopen = useCallback(
    async (task: FollowUpTaskDto) => {
      try {
        await api.reopenTask(task.id);
        push(t("customers.tasks.reopenedToast"), task.subject);
        await load();
      } catch (err) {
        if (err instanceof ApiClientError) push(err.message, undefined, "danger");
      }
    },
    [load, push, t]
  );

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">{t("customers.tasks.title")}</h2>
        <Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("customers.tasks.schedule")}
        </Button>
      </div>

      {error ? (
        <p role="alert" className="text-sm text-action-danger">
          {error}
        </p>
      ) : null}

      {tasks.length === 0 && !adding ? (
        <p className="text-sm text-ink-muted">{t("customers.tasks.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => (
            <li key={task.id} className="rounded border border-line px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-medium text-ink">
                    {task.subject}
                    <Badge tone={taskStatus(task.status).tone}>
                      {t(`status.task.${task.status}` as MessageKey)}
                    </Badge>
                    <Badge tone={taskPriority(task.priority).tone}>
                      {t(`status.priority.${task.priority}` as MessageKey)}
                    </Badge>
                  </p>
                  <p className="text-xs text-ink-muted">
                    {task.assigneeName
                      ? t("customers.tasks.dueTo", { name: task.assigneeName })
                      : t("common.unassigned")}
                    {task.dueAt
                      ? ` · ${t("customers.tasks.dueAt", { date: formatDateTime(task.dueAt) })}`
                      : ""}
                  </p>
                </div>
                {task.status === "completed" ? (
                  <Button size="sm" variant="ghost" onClick={() => void reopen(task)}>
                    <RotateCcw className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />
                    {t("customers.tasks.reopen")}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void complete(task)}
                  >
                    <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    {t("customers.tasks.complete")}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className="mt-3 space-y-3 border-t border-line pt-3">
          <TextInput
            label={t("common.fields.subject")}
            required
            value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
          />
          <TextInput
            label={t("common.fields.notes")}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <SelectInput
              label={t("common.fields.assignTo")}
              options={[
                { value: "", label: t("common.unassigned") },
                ...directory.map((u) => ({ value: u.id, label: u.displayName })),
              ]}
              value={form.assigneeId}
              onChange={(e) => setForm((f) => ({ ...f, assigneeId: e.target.value }))}
            />
            <div className="flex flex-col gap-1.5">
              <label htmlFor="task-due" className="text-sm font-medium text-ink">
                {t("common.fields.dueDate")}
              </label>
              <input
                id="task-due"
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                className="h-11 rounded-[var(--radius-card)] border border-line bg-surface-raised px-3 text-sm text-ink"
              />
            </div>
            <SelectInput
              label={t("common.fields.priority")}
              options={[
                { value: "low", label: t("status.priority.low") },
                { value: "medium", label: t("status.priority.medium") },
                { value: "high", label: t("status.priority.high") },
              ]}
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={submit}
              disabled={busy || !form.subject.trim()}
            >
              {busy ? t("common.saving") : t("customers.tasks.save")}
            </Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
